import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useFormik } from 'formik';
//@ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';

import api, { continueWithEmail } from 'utils/api';
import useSsoLabel from 'utils/useSsoLabel';

import Container from 'components/common/Container';
import TextInput from 'components/common/TextInput';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import MuiContainer from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import CustomLink from 'components/common/CustomLink';
import { useNavigate } from 'react-router-dom';
import { generateRandomPassword } from 'utils/validatePassword';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';

// =============================================================================
const SIGNUP_API = '/public/register';

// =============================================================================
export default function Signup({
  nusEmail,
  onComplete
}: {
  nusEmail?: string;
  onComplete?: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [captchaDone, setCaptchaDone] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [isFormFirstPart, setIsFormFirstPart] = useState(!nusEmail);
  const [snackbar, setSnackbar] = useState('');
  const [modal, setModal] = useState({
    title: ''
  });

  const formik = useFormik({
    initialValues: {
      email: nusEmail ?? '',
      name: '',
      title: '',
      organization: '',
      country: '',
      industry: '',
      agree: false
    },
    onSubmit: async (values) => {
      if (isFormFirstPart) {
        if (!values.email.trim()) {
          setSnackbar('Fields cannot be empty!');
          return;
        }
        try {
          const email = values.email.trim().toLowerCase();
          if (await continueWithEmail(email)) {
            await formik.setFieldValue('email', email);
            setIsFormFirstPart(false);
          } else {
            setRedirecting(true);
          }
        } catch {
          setSnackbar('Unable to check your email. Please try again.');
        }
        return;
      }
      if (
        !values.email ||
        !values.name ||
        !values.title ||
        !values.organization ||
        !values.country ||
        !values.industry
      ) {
        setSnackbar('Fields cannot be empty!');
        return;
      }
      if (!values.agree) {
        setSnackbar('Tick the checkbox to agree to the Data Use Policy.');
        return;
      }

      try {
        const { email, name, title, organization, country, industry } = values;
        const profile = {
          name,
          title,
          field: industry,
          country,
          org: organization
        };
        if (nusEmail) {
          await api.post('/public/oidc/complete', profile);
          await onComplete?.();
          return;
        }
        const res = await api.post(SIGNUP_API, {
          ...profile,
          email,
          password: generateRandomPassword()
        });
        window.myAntsTag?.('SET', 'userId', res.data.userId);
        window.myAntsTag?.('EVENT', 'SIGNUP');
        setModal({
          title: 'Sign up successful'
        });
      } catch (err: any) {
        const errMsg =
          err.response?.data?.message ||
          'Unable to complete registration. Please try again.';
        setSnackbar(
          errMsg.includes('already exists')
            ? 'Email is used by an existing account'
            : errMsg
        );
        if (!nusEmail) setIsFormFirstPart(true);
      }
    }
  });

  const sso = useSsoLabel(formik.values.email, isFormFirstPart);

  useEffect(() => {
    return () => {
      formik.handleReset(null);
      setModal({ title: '' });
    };
  }, []);

  const handleDialogClose = () => {
    setModal({ title: '' });
    navigate('/login');
  };

  return (
    <div>
      <Container style={{ marginBottom: 48, marginTop: 48 }}>
        <MuiContainer fixed>
          {isFormFirstPart ? (
            <Typography variant='h2' color='blue.900'>
              Create an account
            </Typography>
          ) : (
            <Typography variant='h2' color='blue.900'>
              Tell us about yourself
            </Typography>
          )}

          <form
            onSubmit={formik.handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '40px 0'
            }}
          >
            {isFormFirstPart ? (
              <>
                <TextInput
                  variant='outlined'
                  id='email'
                  name='email'
                  type='email'
                  label='Email*'
                  placeholder='email@domain.com'
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  containerStyle={{ width: '100%', maxWidth: 400 }}
                />

                <div style={{ height: 32 }} />

                <Button
                  type='submit'
                  disabled={formik.isSubmitting || redirecting}
                  variant='primary'
                  color='dark'
                  style={{}}
                >
                  {redirecting
                    ? 'Redirecting…'
                    : sso
                      ? 'Continue with SSO'
                      : 'Continue'}
                </Button>
                <Typography
                  color='blue.900'
                  component={'div'}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0 8px',
                    flexWrap: 'wrap',
                    marginTop: 1,
                    fontWeight: '600'
                  }}
                >
                  Already have an account?{' '}
                  <CustomLink to='/login' color='blue.600'>
                    Log In
                  </CustomLink>
                </Typography>
              </>
            ) : (
              <>
                <TextInput
                  variant='outlined'
                  id='name'
                  name='name'
                  label='Name*'
                  placeholder='John Doe'
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  containerStyle={{ width: '100%', maxWidth: 400 }}
                />
                <TextInput
                  variant='outlined'
                  id='title'
                  name='title'
                  label='Job title*'
                  placeholder='Research Fellow'
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  containerStyle={{ width: '100%', maxWidth: 400 }}
                />
                <TextInput
                  variant='outlined'
                  id='organization'
                  name='organization'
                  label='Organization*'
                  placeholder='Axel Corporation'
                  value={formik.values.organization}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.organization &&
                    Boolean(formik.errors.organization)
                  }
                  helperText={
                    formik.touched.organization && formik.errors.organization
                  }
                  containerStyle={{ width: '100%', maxWidth: 400 }}
                />
                <TextInput
                  variant='outlined'
                  id='country'
                  name='country'
                  label='Country*'
                  placeholder='Singapore'
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.country && Boolean(formik.errors.country)
                  }
                  helperText={formik.touched.country && formik.errors.country}
                  containerStyle={{ width: '100%', maxWidth: 400 }}
                />
                <TextInput
                  variant='outlined'
                  id='industry'
                  name='industry'
                  label='Research field or industry*'
                  placeholder='Medical industry'
                  value={formik.values.industry}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.industry && Boolean(formik.errors.industry)
                  }
                  helperText={formik.touched.industry && formik.errors.industry}
                  containerStyle={{ width: '100%', maxWidth: 400 }}
                />
                <FormControlLabel
                  id='agree'
                  name='agree'
                  sx={{ maxWidth: '400px' }}
                  control={
                    <Checkbox
                      color='default'
                      value={formik.values.agree}
                      onChange={formik.handleChange}
                    />
                  }
                  label={
                    <Typography
                      color='blue.900'
                      sx={{ fontWeight: '600' }}
                      fontSize={14}
                    >
                      I have read and agree to the{' '}
                      <CustomLink
                        inline
                        to='/data-use-policy'
                        color='blue.600'
                        fontSize={14}
                        newTab
                      >
                        Ombak Data Use Agreement and Privacy Policy
                      </CustomLink>{' '}
                      which includes the Privacy Policy.
                    </Typography>
                  }
                />
                <div style={{ height: 16 }} />
                {!!import.meta.env.RECAPTCHA_SECRET_KEY && (
                  <ReCAPTCHA
                    sitekey={import.meta.env.RECAPTCHA_CLIENT_KEY}
                    onChange={() => setCaptchaDone(true)}
                  />
                )}
                <div style={{ height: 16 }} />
                <Button
                  type='submit'
                  variant='primary'
                  color='dark'
                  disabled={
                    formik.isSubmitting ||
                    (!captchaDone && !!import.meta.env.RECAPTCHA_SECRET_KEY)
                  }
                >
                  Sign Up
                </Button>
              </>
            )}
          </form>
          <div style={{ height: 40 }} />
        </MuiContainer>
      </Container>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          sx={{
            width: '100%',
            maxWidth: 300
          }}
          onClose={() => setSnackbar('')}
          variant={'filled'}
          severity={'error'}
        >
          {snackbar}
        </Alert>
      </Snackbar>
      <Dialog
        open={!!modal.title}
        onClose={handleDialogClose}
        maxWidth={'laptop'}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='h5' component={'span'}>
            {modal.title}
          </Typography>
          <CheckCircleOutline color='success' />
        </DialogTitle>

        <DialogContent>
          Thank you.
          <br />
          You have successfully signed up with Ombak.
          <br />
          A link to set your password has been sent to the email you signed up
          with. After setting your password, you may then login.
          <br />
          <br />
          The MESN Team
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>OK</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
